/**
 * <recipient-form>
 *
 * Dawn-compatible gift card recipient fields. Field names are Shopify's
 * contract (`Recipient email`, `Recipient name`, `Message`, `Send on`,
 * `__shopify_send_gift_card_to_recipient`, `__shopify_offset`).
 *
 * Disabled fields are omitted from FormData, so an unchecked box does not
 * send recipient properties. Shopify then delivers the gift card to the buyer.
 */
if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.sectionId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.sectionId}`);
        this.emailInput = this.querySelector(`#Recipient-email-${this.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.sectionId}`);
        this.send_onInput = this.querySelector(`#Recipient-send_on-${this.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.sectionId}`);
        this.liveRegion = this.querySelector(`#Recipient-fields-live-region-${this.sectionId}`);
        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.currentProductVariantId = this.dataset.productVariantId;
        this.form = this.closest('form');

        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        this._setSendOnBounds();

        if (this.checkboxInput) this.checkboxInput.disabled = false;
        if (this.hiddenControlField) this.hiddenControlField.disabled = true;

        this._onChange = this.onChange.bind(this);
        this._onSubmit = this.onSubmit.bind(this);
        this.addEventListener('change', this._onChange);
        this.onChange();
      }

      connectedCallback() {
        this.form = this.form || this.closest('form');
        this.form?.addEventListener('submit', this._onSubmit, true);

        this._onVariantChange = (event) => {
          const data = event?.detail || event;
          if (!data || data.sectionId !== this.sectionId || !data.variant?.id) return;
          this.currentProductVariantId = String(data.variant.id);
        };
        document.addEventListener('variant:change', this._onVariantChange);

        if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this._unsubCartUpdate = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event?.source !== 'product-form') return;
            if (String(event.productVariantId) !== String(this.currentProductVariantId)) return;
            this.resetRecipientForm();
          });

          this._unsubCartError = window.subscribe(window.PUB_SUB_EVENTS.cartError, (event) => {
            if (event?.source !== 'product-form') return;
            if (String(event.productVariantId) !== String(this.currentProductVariantId)) return;
            this.displayErrorMessage(event.message, event.errors);
          });
        }
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this._onSubmit, true);
        if (this._onVariantChange) document.removeEventListener('variant:change', this._onVariantChange);
        this._unsubCartUpdate?.();
        this._unsubCartError?.();
      }

      onChange() {
        if (!this.checkboxInput) return;
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.classList.add('is-open');
          if (this.liveRegion) this.liveRegion.textContent = 'Formulier voor de ontvanger is geopend.';
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.classList.remove('is-open');
          if (this.liveRegion) this.liveRegion.textContent = 'Formulier voor de ontvanger is gesloten.';
        }
      }

      onSubmit(event) {
        if (!this.checkboxInput?.checked) return;
        if (this.validate()) return;
        event.preventDefault();
        // Other submit listeners may still add .loading during this event.
        // That class sets pointer-events: none, so the button would stay stuck.
        const button = this.form?.querySelector('.btn-cta[type="submit"]');
        queueMicrotask(() => {
          if (!button) return;
          button.classList.remove('loading');
          button.removeAttribute('aria-busy');
          if (button.dataset.prevDisabled !== 'true') button.removeAttribute('disabled');
        });
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.send_onInput].filter(Boolean);
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          field.value = '';
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = false;
        });
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = true;
        });
      }

      _setSendOnBounds() {
        if (!this.send_onInput) return;
        const today = new Date();
        const max = new Date();
        max.setDate(max.getDate() + 90);
        this.send_onInput.min = this._isoDate(today);
        this.send_onInput.max = this._isoDate(max);
      }

      _isoDate(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
      }

      validate() {
        const errors = {};
        const email = this.emailInput?.value.trim() || '';
        if (!email) {
          errors.email = ['Vul het e-mailadres van de ontvanger in.'];
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          errors.email = ['Vul een geldig e-mailadres in.'];
        }

        if ((this.nameInput?.value.length || 0) > 255) {
          errors.name = ['De naam mag maximaal 255 tekens zijn.'];
        }
        if ((this.messageInput?.value.length || 0) > 200) {
          errors.message = ['Het bericht mag maximaal 200 tekens zijn.'];
        }

        const sendOn = this.send_onInput?.value;
        if (sendOn) {
          if (sendOn < this.send_onInput.min || sendOn > this.send_onInput.max) {
            errors.send_on = ['Kies een datum vandaag of binnen 90 dagen.'];
          }
        }

        if (Object.keys(errors).length === 0) return true;
        this.displayErrorMessage('Controleer de gegevens van de ontvanger.', errors);
        const firstInvalid = this.querySelector('[aria-invalid="true"]');
        firstInvalid?.focus();
        return false;
      }

      translateError(key, value) {
        const text = String(value || '');
        const lower = text.toLowerCase();
        if (key === 'email' && (lower.includes('blank') || lower.includes('required') || lower.includes('missing'))) {
          return 'Vul het e-mailadres van de ontvanger in.';
        }
        if (key === 'email' && lower.includes('invalid')) return 'Vul een geldig e-mailadres in.';
        if (key === 'name' && lower.includes('long')) return 'De naam mag maximaal 255 tekens zijn.';
        if (key === 'message' && lower.includes('long')) return 'Het bericht mag maximaal 200 tekens zijn.';
        if (key === 'send_on') return 'Kies een datum vandaag of binnen 90 dagen.';
        return text;
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (!this.errorMessageWrapper) return;
        this.errorMessageWrapper.hidden = false;

        if (body && typeof body === 'object') {
          if (this.errorMessage) {
            this.errorMessage.textContent = 'Controleer de gegevens van de ontvanger.';
          }
          Object.entries(body).forEach(([key, value]) => {
            const messages = (Array.isArray(value) ? value : [value]).map((item) => this.translateError(key, item));
            const message = messages.join(', ');
            const errorMessageId = `RecipientForm-${key}-error-${this.sectionId}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (errorTextElement) errorTextElement.textContent = message;
            errorMessageElement?.classList.remove('hidden');

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(`#Recipient-${key}-${this.sectionId}`, message));
            }

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;
            inputElement.setAttribute('aria-invalid', 'true');
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
          this.errorMessageWrapper.focus();
          return;
        }

        if (this.errorMessage) this.errorMessage.textContent = this.translateError(null, body || title);
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.setAttribute('href', target);
        link.textContent = message;
        li.appendChild(link);
        return li;
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) this.errorMessageWrapper.hidden = true;
        if (this.errorMessageList) this.errorMessageList.innerHTML = '';
        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.textContent = '';
        });
        this.inputFields().forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', 'false');
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (!this.checkboxInput?.checked) return;
        this.checkboxInput.checked = false;
        this.clearInputFields();
        this.clearErrorMessage();
        this.disableInputFields();
        this.classList.remove('is-open');
      }
    }
  );
}
